import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { getUserInfo } from "../service/auth.service";
import { setUserInfo } from "../slices/authSlice";
import { useAppDispatch } from "./redux-hooks";

interface UseUserInfoReturn {
  fetchUserInfo: () => Promise<any>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useUserInfo = (): UseUserInfoReturn => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserInfo = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getUserInfo();
      const data = await response.data;
      dispatch(setUserInfo(data));
      return response;
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : "Failed to fetch user info";
      setError(errorMessage);
      // 401 is the "session expired / logged out" path, not a bug. The axios
      // interceptor handles the redirect; logging here just floods Sentry.
      if (!axios.isAxiosError(e) || e.response?.status !== 401) {
        console.error("Error fetching user info:", e);
      }
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const refetch = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  return {
    fetchUserInfo,
    isLoading,
    error,
    refetch,
  };
};

export default useUserInfo;
